import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

export default function DeleteAccountDialog({ open, onOpenChange, userEmail, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const matches = confirmText.trim().toLowerCase() === (userEmail || "").toLowerCase();

  const handleDelete = async () => {
    if (!matches) return;
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      toast.success("Your account has been deleted");
      onOpenChange(false);
      if (onDeleted) onDeleted();
    } catch (e) {
      toast.error("Couldn't delete account — please try again");
    } finally {
      setDeleting(false);
      setConfirmText("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!deleting) { onOpenChange(v); if (!v) setConfirmText(""); } }}>
      <DialogContent
        className="sm:max-w-lg bg-white border-[#D4C7E1] rounded-2xl"
        data-testid="delete-account-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-full bg-[#F6ECEC] flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-[#C28A8A]" />
            </div>
            <DialogTitle className="text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              Delete your account?
            </DialogTitle>
          </div>
          <DialogDescription className="text-[#75737A] pt-2 leading-relaxed">
            This will permanently delete your account, all of your prayers,
            your answered prayers, and any sign-in history.
            <br />
            <b className="text-[#2C2A2E]">This cannot be undone.</b>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2">
          <Label htmlFor="confirm-email" className="text-xs uppercase tracking-[0.18em] text-[#75737A]">
            Type your email to confirm
          </Label>
          <Input
            id="confirm-email"
            data-testid="delete-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={userEmail}
            autoComplete="off"
            className="bg-[#FAF9F6] border-[#D4C7E1] rounded-xl h-11"
          />
        </div>

        <DialogFooter className="gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            className="rounded-full border-[#D4C7E1]"
            onClick={() => onOpenChange(false)}
            disabled={deleting}
            data-testid="delete-cancel-btn"
          >
            Keep my account
          </Button>
          <Button
            type="button"
            onClick={handleDelete}
            disabled={!matches || deleting}
            className="rounded-full px-6 bg-[#C28A8A] text-white hover:bg-[#A87373] disabled:opacity-50"
            data-testid="delete-confirm-btn"
          >
            {deleting ? "Deleting…" : "Delete forever"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
